import { cn } from "@/lib/utils";
import { useUiStore } from "@/stores/ui-store";
import { useAuth } from "@/hooks/use-auth";
import { usePermissionsOptional } from "@/hooks/use-permissions";
import { useBrands } from "@/hooks/use-brands";
import { useInboxUnread } from "@/hooks/use-inbox-unread";
import { UnreadBadge } from "@/components/inbox/unread-badge";
import { useTranslation } from "react-i18next";
import {
  BarChart3,
  Instagram,
  Bell,
  Bot,
  Building,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Clock,
  Cpu,
  DollarSign,
  Eye,
  FileCode,
  FolderKanban,
  History,
  Sparkles,
  Image as ImageLib,
  Inbox as InboxIcon,
  LayoutTemplate,
  Lightbulb,
  Link2,
  LogOut,
  Megaphone,
  Palette,
  Settings,
  ShieldCheck,
  Users,
  Zap,
} from "lucide-react";
import { Link, useParams, useLocation } from "react-router-dom";
import { BrandSelector } from "./brand-selector";

interface NavItem {
  href: string;
  label: string;
  icon: React.ElementType;
  badge?: number;
}

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar } = useUiStore();
  const location = useLocation();
  const pathname = location.pathname;
  const params = useParams<{ brandSlug: string }>();
  const brandSlug = params.brandSlug;
  const { user, logout } = useAuth();
  const permissions = usePermissionsOptional();
  const { t } = useTranslation("nav");
  const { t: tc } = useTranslation("common");

  // Resolve brand id from the slug so we can hit the (brand-scoped) unread-count
  // endpoint. The /unread-count call is the cheapest way to detect "brand has
  // an IG account": it returns 404 INBOX_DISABLED when no IG account exists,
  // which we use to gracefully hide the Inbox link. This avoids an additional
  // round-trip — we'd be polling unread regardless when the inbox is enabled.
  const { data: brands } = useBrands();
  const currentBrand = brandSlug ? brands?.find((b) => b.slug === brandSlug) : undefined;
  const inboxUnread = useInboxUnread(currentBrand?.id);
  // Hide the Inbox entry when the API replied 404 (no IG account). React Query
  // surfaces that as isError + no data because we threw — except we returned
  // null on 404, so isError is false and data is null. Either way, we only
  // show the link when data is non-null OR while we're still loading the first
  // time (avoids flashing in/out).
  const hasInbox = inboxUnread.data != null;
  const inboxDmUnread = inboxUnread.data?.dms ?? 0;
  const inboxCommentUnread = inboxUnread.data?.comments ?? 0;
  const inboxTotalUnread = inboxDmUnread + inboxCommentUnread;

  const prefix = brandSlug ? `/${brandSlug}` : "";

  const contentNav: NavItem[] = [
    ...(brandSlug ? [{ href: `${prefix}/compose`, label: t("sidebar.compose"), icon: Sparkles }] : []),
    ...(brandSlug ? [{ href: `${prefix}/quick-post`, label: t("sidebar.quickPost"), icon: Zap }] : []),
    ...(brandSlug ? [{ href: `${prefix}/quick-post-pro`, label: t("sidebar.postBuilder"), icon: LayoutTemplate }] : []),
    { href: `${prefix}`, label: t("projects"), icon: FolderKanban },
    { href: `${prefix}/ideas`, label: t("ideas"), icon: Lightbulb },
    { href: `${prefix}/review`, label: t("review"), icon: ClipboardCheck },
    { href: `${prefix}/activity`, label: t("activity"), icon: History },
    { href: `${prefix}/calendar`, label: t("calendar"), icon: CalendarDays },
    ...(brandSlug ? [{ href: `${prefix}/library`, label: t("sidebar.library"), icon: ImageLib }] : []),
    ...(brandSlug && hasInbox
      ? [
          {
            href: `${prefix}/inbox`,
            label: t("sidebar.inbox"),
            icon: InboxIcon,
            badge: inboxTotalUnread,
          } as NavItem & { badge?: number },
        ]
      : []),
  ];

  const analyticsNav: NavItem[] = permissions?.canViewAnalytics
    ? [
        { href: `${prefix}/analytics`, label: t("analytics"), icon: BarChart3 },
        { href: `${prefix}/analytics/instagram`, label: t("sidebar.instagram"), icon: Instagram },
        { href: `${prefix}/ads`, label: t("sidebar.ads"), icon: Megaphone },
        { href: `${prefix}/competitors`, label: t("sidebar.competitors"), icon: Eye },
      ]
    : [];

  const settingsNav: NavItem[] = [
    { href: `${prefix}/settings/brand`, label: t("sidebar.brand"), icon: Building },
    { href: `${prefix}/settings/kit`, label: t("kit"), icon: Palette },
    { href: `${prefix}/settings/ai`, label: t("ai"), icon: Cpu },
    ...(permissions?.canManageBrand
      ? [{ href: `${prefix}/settings/ai-prompts`, label: t("sidebar.aiPrompts"), icon: FileCode }]
      : []),
    { href: `${prefix}/settings/scheduling`, label: t("sidebar.scheduling"), icon: Clock },
    { href: `${prefix}/settings/automation`, label: t("sidebar.automation"), icon: Bot },
    ...(permissions?.canViewAiCosts
      ? [{ href: `${prefix}/settings/ai-usage`, label: t("ai-usage"), icon: DollarSign }]
      : []),
    { href: `${prefix}/settings/team`, label: t("team"), icon: Users },
    { href: `${prefix}/settings/accounts`, label: t("accounts"), icon: Link2 },
    ...(brandSlug && hasInbox
      ? [{ href: `${prefix}/settings/inbox`, label: t("sidebar.inbox"), icon: InboxIcon }]
      : []),
    { href: `/settings/notifications`, label: t("notifications"), icon: Bell },
  ];

  const adminNav: NavItem[] = [
    ...(user?.isSuperAdmin ? [{ href: "/admin/users", label: t("sidebar.manageUsers"), icon: ShieldCheck }] : []),
    ...(user?.isOrgOwner ? [{ href: "/admin/prompts", label: t("sidebar.globalPrompts"), icon: FileCode }] : []),
  ];

  function isActive(href: string): boolean {
    if (href === prefix || href === `${prefix}/`) {
      return pathname === prefix || pathname === `${prefix}/`;
    }
    return pathname === href || pathname.startsWith(href + "/");
  }

  const userInitial = user?.name?.charAt(0)?.toUpperCase() ?? "?";

  return (
    <aside
      className={cn(
        "flex flex-col flex-shrink-0 h-screen bg-surface-0 border-r border-border overflow-hidden",
        "transition-[width] duration-200 ease-in-out",
        sidebarCollapsed ? "w-14" : "w-[220px]"
      )}
    >
      {/* ── Header ───────────────────────────────────────── */}
      <div
        className={cn(
          "flex-shrink-0 border-b border-border",
          sidebarCollapsed ? "px-2 py-3 flex justify-center" : "px-3.5 py-3"
        )}
      >
        {sidebarCollapsed ? (
          <div className="w-7 h-7 bg-brand rounded-md flex items-center justify-center">
            <span className="text-[11px] font-bold text-white leading-none">C</span>
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            <span className="text-[12px] font-bold text-foreground tracking-tight leading-none">
              Content OS
            </span>
            {brandSlug && <BrandSelector currentSlug={brandSlug} />}
          </div>
        )}
      </div>

      {/* ── Nav ──────────────────────────────────────────── */}
      <nav className="flex-1 overflow-y-auto py-2 px-2 flex flex-col gap-0.5">
        {/* CONTENT group */}
        {!sidebarCollapsed && (
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em] px-2 pt-1.5 pb-1">
            {t("sidebar.sections.content")}
          </p>
        )}
        {sidebarCollapsed && <div className="h-2" />}

        {contentNav.map((item) => (
          <NavLink
            key={item.href}
            item={item}
            active={isActive(item.href)}
            collapsed={sidebarCollapsed}
          />
        ))}

        {/* ANALYTICS group */}
        {analyticsNav.length > 0 && (
          <>
            {!sidebarCollapsed ? (
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em] px-2 pt-4 pb-1">
                {t("sidebar.sections.analytics")}
              </p>
            ) : (
              <div className="h-3" />
            )}
            {analyticsNav.map((item) => (
              <NavLink
                key={item.href}
                item={item}
                active={isActive(item.href)}
                collapsed={sidebarCollapsed}
              />
            ))}
          </>
        )}

        {/* SETTINGS group */}
        {!sidebarCollapsed ? (
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em] px-2 pt-4 pb-1">
            {t("sidebar.sections.settings")}
          </p>
        ) : (
          <div className="h-3" />
        )}
        {settingsNav.map((item) => (
          <NavLink
            key={item.href}
            item={item}
            active={isActive(item.href)}
            collapsed={sidebarCollapsed}
          />
        ))}

        {/* ADMIN group */}
        {adminNav.length > 0 && (
          <>
            {!sidebarCollapsed ? (
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em] px-2 pt-4 pb-1">
                {t("sidebar.sections.admin")}
              </p>
            ) : (
              <div className="h-3" />
            )}
            {adminNav.map((item) => (
              <NavLink
                key={item.href}
                item={item}
                active={isActive(item.href)}
                collapsed={sidebarCollapsed}
              />
            ))}
          </>
        )}
      </nav>

      {/* ── Footer ───────────────────────────────────────── */}
      <div className="flex-shrink-0 border-t border-border p-2 flex flex-col gap-1">
        {/* User row — hidden when collapsed */}
        {!sidebarCollapsed && (
          <div className="flex items-center gap-2 px-2 py-1.5">
            <span className="w-[26px] h-[26px] rounded-full bg-brand-subtle flex items-center justify-center text-[10px] font-semibold text-brand-subtle-text flex-shrink-0">
              {userInitial}
            </span>
            <span className="flex-1 text-[12px] text-muted-foreground truncate">
              {user?.name ?? ""}
            </span>
            <Link
              to={`${prefix}/settings/kit`}
              title={tc("actions.settings")}
              className="text-muted-foreground hover:text-foreground transition-colors p-0.5"
            >
              <Settings size={13} />
            </Link>
            <button
              onClick={() => logout()}
              title={tc("actions.signOut")}
              className="text-muted-foreground hover:text-foreground transition-colors p-0.5"
            >
              <LogOut size={13} />
            </button>
          </div>
        )}

        {/* Collapse toggle */}
        <button
          onClick={toggleSidebar}
          title={sidebarCollapsed ? t("sidebar.expand") : t("sidebar.collapse")}
          aria-expanded={!sidebarCollapsed}
          aria-label={sidebarCollapsed ? t("sidebar.expand") : t("sidebar.collapse")}
          className={cn(
            "flex items-center justify-center rounded-md h-6 transition-colors",
            "text-muted-foreground hover:text-foreground hover:bg-surface-2",
            sidebarCollapsed ? "w-full" : "w-7 self-end"
          )}
        >
          {sidebarCollapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
        </button>
      </div>
    </aside>
  );
}

// ── NavLink ───────────────────────────────────────────────────────────────────

function NavLink({
  item,
  active,
  collapsed,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
}) {
  return (
    <Link
      to={item.href}
      title={collapsed ? item.label : undefined}
      aria-label={collapsed ? item.label : undefined}
      className={cn(
        "flex items-center gap-2 rounded-md py-[7px] text-[13px] transition-colors",
        collapsed ? "px-0 justify-center w-full" : "pr-2",
        active
          ? "bg-accent text-accent-foreground font-medium border-l-2 border-primary pl-[6px]"
          : "pl-2 text-muted-foreground hover:bg-surface-2 hover:text-foreground"
      )}
    >
      <item.icon
        size={15}
        className="flex-shrink-0"
        strokeWidth={active ? 2.5 : 2}
      />
      {!collapsed && (
        <span className="flex-1 flex items-center justify-between gap-2">
          <span>{item.label}</span>
          {item.badge ? <UnreadBadge count={item.badge} /> : null}
        </span>
      )}
    </Link>
  );
}
